import { FreightType, type FreightDto } from '@/ApiClient';
import { useApi } from '@/lib/api';
import { useGlobalStore } from '@/store/global-store-base';
import { useHierarchy } from '@/store/hierarchy';
import isNil from 'lodash/isNil';
import { defineStore } from 'pinia';
import { useSuppliers } from './suppliers';
import { useSeasons } from './seasons';
import { useInPriceConfigs } from './in-price-configs';
import { useDestinations } from './destinations';

export const useFreights = defineStore('freights', () => {
  const api = useApi();
  const globalStoreBase = useGlobalStore({
    load: api.freight_GetAll,
    dto: 'FreightDto',
    signalR: 'FreightUpdate',
  });

  /**
   * Resolves the freight row for a given country, mode of transport and hierarchy node.
   * Country and ModeOfTransport must match exactly (no fallback).
   * Traverses up the hierarchy tree to find a row for the given season.
   * If season is specified but nothing is found, falls back to null-season.
   * Rows failing `hasValue` are skipped, so a node configured only as a percentage does not
   * shadow an ancestor's charge (and vice versa). Mirrors FreightService.Resolve on the backend.
   */
  function resolve(
    countryId: number,
    modeOfTransportId: number,
    hierarchyNodeId: number,
    seasonId: number | null | undefined,
    destinationId: number | undefined,
    hasValue: (freight: FreightDto) => boolean
  ): FreightDto | undefined {
    const hierarchy = useHierarchy();
    const nodeAndAncestors = hierarchy.getNodeAndAncestors(hierarchyNodeId);

    // Normalize seasonId: treat null and undefined the same
    const normalizedSeasonId = seasonId ?? undefined;
    const resolvedDestinationId = destinationId ?? useDestinations().defaultDestination?.id;

    const matchingFreights = globalStoreBase.loaded.value.filter(
      (f) =>
        f.countryId === countryId &&
        f.modeOfTransportId === modeOfTransportId &&
        f.destinationId === resolvedDestinationId &&
        hasValue(f)
    );

    // First try with the specific season
    for (const node of nodeAndAncestors) {
      const freight = matchingFreights.find((f) => {
        const freightSeasonId = f.seasonId ?? undefined;
        return f.hierarchyNodeId === node.id && freightSeasonId === normalizedSeasonId;
      });
      if (freight) {
        return freight;
      }
    }

    // If season was specified but nothing found, try with null-season
    if (!isNil(normalizedSeasonId)) {
      for (const node of nodeAndAncestors) {
        const freight = matchingFreights.find((f) => f.hierarchyNodeId === node.id && isNil(f.seasonId));
        if (freight) {
          return freight;
        }
      }
    }

    return undefined;
  }

  /** The freight percentage, for seasons configured as FreightType.Percentage. */
  function getFreight(
    countryId: number,
    modeOfTransportId: number,
    hierarchyNodeId: number,
    seasonId?: number | null,
    destinationId?: number
  ): number | undefined {
    return resolve(countryId, modeOfTransportId, hierarchyNodeId, seasonId, destinationId, (f) => !isNil(f.rate))?.rate;
  }

  /**
   * The flat freight charge, for seasons configured as FreightType.Charge.
   * A row without a currency cannot produce a usable charge, so it is skipped like an unset amount.
   */
  function getFreightCharge(
    countryId: number,
    modeOfTransportId: number,
    hierarchyNodeId: number,
    seasonId?: number | null,
    destinationId?: number
  ): { amount: number; currencyId: number } | undefined {
    const freight = resolve(
      countryId,
      modeOfTransportId,
      hierarchyNodeId,
      seasonId,
      destinationId,
      (f) => !isNil(f.amount) && !isNil(f.currencyId)
    );
    return freight ? { amount: freight.amount!, currencyId: freight.currencyId! } : undefined;
  }

  /**
   * Whether the current freight value differs from what the MOT lookup table would give, i.e.
   * whether the "reset to the lookup value" affordance should be offered.
   *
   * For a charge, the currency is part of the value: the same amount in another currency is a
   * different freight cost, so it counts as unlinked.
   */
  const isUnlinked = (
    supplierId: number | undefined,
    hierarchyNodeId: number | undefined,
    seasonIds: number[] | undefined,
    freightValue: number | undefined,
    destinationId?: number,
    freightCurrencyId?: number
  ): boolean => {
    if (!supplierId || !seasonIds) {
      return false;
    }

    const supplier = useSuppliers().getById(supplierId);
    const season = useSeasons().getLatestSeasonFromIds(seasonIds);
    const freightType = useInPriceConfigs().getBySeasonAndDestination(season?.id, destinationId)?.freightType;

    if (!supplier?.country.id || !supplier?.modeOfTransportId || !hierarchyNodeId || isNil(freightType)) {
      return false;
    }

    if (freightType === FreightType.Percentage) {
      const defaultRate = getFreight(
        supplier.country.id,
        supplier.modeOfTransportId,
        hierarchyNodeId,
        season?.id,
        destinationId
      );

      // No default available - don't show reset button
      if (defaultRate === undefined) {
        return false;
      }

      // Default exists (can be 0) - show reset if current differs from default
      return defaultRate !== freightValue;
    }

    const defaultCharge = getFreightCharge(
      supplier.country.id,
      supplier.modeOfTransportId,
      hierarchyNodeId,
      season?.id,
      destinationId
    );

    if (defaultCharge === undefined) {
      return false;
    }

    return defaultCharge.amount !== freightValue || defaultCharge.currencyId !== freightCurrencyId;
  };

  return { ...globalStoreBase, getFreight, getFreightCharge, isUnlinked };
});
